/** Original synthesis facts, not the current (possibly trimmed/moved) timeline window. */
export type CompanySpeechSource = {
  version: 1;
  kind: 'ledger-line';
  ledgerSource: 'agent-lines' | 'restored';
  trackUrl: string;
  sourceUrl: string;
  lineIndex: number;
  trackStartSec: number;
  durationSec: number;
  text: string;
  voiceId?: string;
  paramSig?: string;
  slotSec?: number;
  state?: 'ok' | 'edited' | 'failed';
  base?: {speed: number; emotion: string};
};

const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const nonnegative = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;
const http = (v: unknown): v is string => {
  if (typeof v !== 'string') return false;
  try { const u = new URL(v); return ['https:', 'http:'].includes(u.protocol) && !u.username && !u.password; } catch { return false; }
};

/** A replaced audio file cannot inherit the old file's voice identity. Unknown records stay unknown. */
export function readCompanySpeechSource(value: unknown, sourceUrl: unknown): CompanySpeechSource | null {
  if (!record(value) || value.version !== 1 || value.kind !== 'ledger-line'
    || !['agent-lines', 'restored'].includes(String(value.ledgerSource))
    || !http(value.trackUrl) || !http(value.sourceUrl) || value.sourceUrl !== sourceUrl
    || !nonnegative(value.lineIndex) || !Number.isSafeInteger(value.lineIndex)
    || !nonnegative(value.trackStartSec) || !nonnegative(value.durationSec) || value.durationSec <= 0
    || typeof value.text !== 'string' || !value.text.trim()) return null;
  if (value.voiceId !== undefined && (typeof value.voiceId !== 'string' || !value.voiceId)) return null;
  if (value.paramSig !== undefined && (typeof value.paramSig !== 'string' || !value.paramSig)) return null;
  if (value.slotSec !== undefined && (!nonnegative(value.slotSec) || value.slotSec <= 0)) return null;
  if (value.state !== undefined && !['ok', 'edited', 'failed'].includes(String(value.state))) return null;
  if (value.base !== undefined && (!record(value.base) || !nonnegative(value.base.speed) || value.base.speed <= 0
    || typeof value.base.emotion !== 'string' || !value.base.emotion)) return null;
  return {
    version: 1, kind: 'ledger-line', ledgerSource: value.ledgerSource === 'agent-lines' ? 'agent-lines' : 'restored',
    trackUrl: value.trackUrl, sourceUrl: value.sourceUrl, lineIndex: value.lineIndex,
    trackStartSec: value.trackStartSec, durationSec: value.durationSec, text: value.text,
    ...(typeof value.voiceId === 'string' ? {voiceId: value.voiceId} : {}),
    ...(typeof value.paramSig === 'string' ? {paramSig: value.paramSig} : {}),
    ...(typeof value.slotSec === 'number' ? {slotSec: value.slotSec} : {}),
    ...(value.state === 'ok' || value.state === 'edited' || value.state === 'failed' ? {state: value.state} : {}),
    ...(record(value.base) && typeof value.base.speed === 'number' && typeof value.base.emotion === 'string'
      ? {base: {speed: value.base.speed, emotion: value.base.emotion}} : {}),
  };
}
